import React, { useState, useCallback, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { HiX, HiPlus, HiLink } from "react-icons/hi";
import { Session, TrainingLog } from "../../types/post";

interface SessionLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  // The completed session being linked.
  session: Session & { id: string };
  // Training logs on the same day the session can be linked to.
  trainingLogs: (TrainingLog & { id: string })[];
  // Link the session to an existing training log.
  onLinkToLog: (
    sessionId: string,
    trainingLogId: string,
  ) => void | Promise<void>;
  // Create a new training log pre-linked to this session.
  onCreateLog: (session: Session & { id: string }) => void;
  // Resolve the display title for the session.
  getSessionTitle: (session: Session) => string;
}

const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const formatSessionDate = (plannedDate: string): string => {
  const date = new Date(plannedDate);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "long",
    day: "numeric",
  });
};

// Mirrors ActivityLinkModal: lets the athlete create a new training log for a
// completed session or link it to an existing one for the day.
const SessionLinkModal: React.FC<SessionLinkModalProps> = ({
  isOpen,
  onClose,
  session,
  trainingLogs,
  onLinkToLog,
  onCreateLog,
  getSessionTitle,
}) => {
  const [isLinking, setIsLinking] = useState(false);

  const activityCount = session.ActivityMeasurementList?.length ?? 0;
  const sessionDate = formatSessionDate(session.plannedDate);

  const handleLinkExisting = useCallback(
    async (trainingLogID: string) => {
      try {
        setIsLinking(true);
        await onLinkToLog(session.id, trainingLogID);
        onClose();
      } catch (error) {
        console.error("Error linking session to training log:", error);
      } finally {
        setIsLinking(false);
      }
    },
    [onLinkToLog, session.id, onClose],
  );

  const handleCreateNew = useCallback(() => {
    onCreateLog(session);
    onClose();
  }, [onCreateLog, session, onClose]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="p-2 w-full max-w-md transform overflow-hidden rounded-lg bg-base-100 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-center p-6 border-b border-base-200">
                  <div>
                    <Dialog.Title className="text-xl font-bold font-grotesk text-base-content">
                      Link Session
                    </Dialog.Title>
                    <p className="text-sm text-base-content/80 mt-1">
                      {getSessionTitle(session)} • {activityCount}{" "}
                      {activityCount === 1 ? "activity" : "activities"}
                      {sessionDate && (
                        <>
                          {" "}
                          • <span className="font-medium">{sessionDate}</span>
                        </>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-base-200 rounded-full transition-colors"
                  >
                    <HiX className="w-5 h-5 text-base-content/60" />
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  {/* Create New Training Log */}
                  <button
                    onClick={handleCreateNew}
                    disabled={isLinking}
                    className="w-full flex items-center justify-center space-x-3 p-4 border-2 border-dashed border-base-300 rounded-lg hover:border-primary hover:bg-primary hover:bg-opacity-5 transition-colors group"
                  >
                    <HiPlus className="w-5 h-5 text-base-content/50 group-hover:text-primary" />
                    <span className="text-base-content/80 group-hover:text-primary font-medium">
                      Create New Training Log
                    </span>
                  </button>

                  {/* Link to Existing Training Log */}
                  {trainingLogs.length > 0 && (
                    <>
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-base-300" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-base-100 text-base-content/60">
                            or link to existing
                          </span>
                        </div>
                      </div>

                      <div className="space-y-4 max-h-60 overflow-y-auto">
                        {trainingLogs.map((trainingLog) => (
                          <button
                            key={trainingLog.id}
                            onClick={() => handleLinkExisting(trainingLog.id)}
                            disabled={isLinking}
                            className="w-full flex items-center justify-between p-3 border border-base-300 bg-base-200 rounded-lg hover:border-primary hover:bg-primary hover:bg-opacity-5 transition-colors group"
                          >
                            <div className="flex items-center space-x-3">
                              <HiLink className="w-4 h-4 text-base-content/50 group-hover:text-primary" />
                              <div className="text-left">
                                <p className="text-sm font-medium text-base-content">
                                  {trainingLog.notes ||
                                    trainingLog.tag?.[0] ||
                                    "Training Session"}
                                </p>
                                <p className="text-xs text-base-content/60">
                                  {formatDuration(trainingLog.duration)} •{" "}
                                  <strong>{Math.round(trainingLog.load)}</strong>{" "}
                                  load
                                </p>
                              </div>
                            </div>
                            {isLinking && (
                              <div className="loading loading-spinner loading-sm"></div>
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  {trainingLogs.length === 0 && (
                    <div className="text-center py-4">
                      <p className="text-sm text-base-content/60">
                        No training sessions available to link
                      </p>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SessionLinkModal;
